import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ru";

import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import ErrorHandler from "../../../components/common/ErrorHandler";
import {
    ErrorHandlerContext,
    ErrorHandlerContextProvider,
} from "../../../contexts/ErrorHandlerContext";
import useUser, {
    isUserDoctor,
    getAuthorized,
    postAuthorized,
    deleteAuthorized,
    patchAuthorized,
} from "../../../hooks/User";
import { ROUTES } from "../../../routes";
import {
    getPatientDisplayName,
    getPatientInitials,
    hasPatientName,
} from "../../../utils/patientUtils";
import {
    ChevronLeftIcon,
    SearchIcon,
    DownloadIcon,
    TrashIcon,
    XIcon,
    PlusIcon,
    UserPlusIcon,
    UserIcon,
    FolderIcon,
} from "../../../components/icons";

import "./patient-group-detail.css";

dayjs.locale("ru");

const DEFAULT_PAGE_SIZE = 10;

function FolderIconLocal() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>
        </svg>
    );
}

function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText, isLoading, danger }) {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <TrashIcon size={32} />
                </div>
                <h3>{title}</h3>
                <p>{message}</p>
                <div className="confirm-actions">
                    <button className="btn-secondary" onClick={onClose} disabled={isLoading}>Отмена</button>
                    <button className={`btn-confirm ${danger ? "btn-danger" : ""}`} onClick={onConfirm} disabled={isLoading}>
                        {isLoading ? "Загрузка..." : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PatientAddModal({ isOpen, onClose, groupId, onAdded, isLoading }) {
    const { setErrorList } = useContext(ErrorHandlerContext);
    const [patients, setPatients] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isFetching, setIsFetching] = useState(false);
    const [addingId, setAddingId] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        setSearchQuery("");
        setAddingId(null);
        setIsFetching(true);
        getAuthorized("/doctor/my_patients", { params: { st: 0, limit: 1000 } })
            .then((response) => {
                const list = response.data?.patients || [];
                setPatients(list);
                setFiltered(list);
            })
            .catch((error) => {
                console.error("Failed to load my patients:", error);
                setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при загрузке пациентов"]);
            })
            .finally(() => setIsFetching(false));
    }, [isOpen, setErrorList]);

    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) {
            setFiltered(patients);
            return;
        }
        setFiltered(
            patients.filter((p) => {
                const name = getPatientDisplayName(p).toLowerCase();
                return name.includes(q) || (p.email && p.email.toLowerCase().includes(q));
            })
        );
    }, [searchQuery, patients]);

    const handleAdd = async (patient) => {
        setAddingId(patient.id);
        try {
            await postAuthorized(`/doctor/patient_groups/${groupId}/patients`, { patient_id: patient.id });
            onAdded();
        } catch (error) {
            console.error("Failed to add patient to group:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при добавлении пациента"]);
        } finally {
            setAddingId(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-form modal-wide" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Добавить пациентов в группу</h2>
                    <button className="modal-close" onClick={onClose} disabled={isLoading}><XIcon /></button>
                </div>
                <div className="modal-body">
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Поиск по имени или email..."
                            disabled={isFetching}
                        />
                        {searchQuery && (
                            <button className="search-clear" onClick={() => setSearchQuery("")} title="Очистить поиск">×</button>
                        )}
                    </div>
                    {isFetching ? (
                        <div className="modal-loading"><div className="spinner"></div><p>Загрузка пациентов...</p></div>
                    ) : filtered.length === 0 ? (
                        <div className="modal-empty">
                            <p>{searchQuery ? "Пациенты не найдены" : "Нет доступных пациентов для добавления"}</p>
                        </div>
                    ) : (
                        <div className="patient-add-list">
                            {filtered.map((patient) => (
                                <div key={patient.id} className="patient-add-item">
                                    <div className="patient-add-avatar">
                                        {hasPatientName(patient) ? getPatientInitials(patient) : <UserIcon size={18} />}
                                    </div>
                                    <div className="patient-add-info">
                                        <span className="patient-add-name">{getPatientDisplayName(patient)}</span>
                                        {patient.email && <span className="patient-add-email">{patient.email}</span>}
                                    </div>
                                    <button
                                        className="btn-action btn-add"
                                        onClick={() => handleAdd(patient)}
                                        disabled={addingId === patient.id || isLoading}
                                    >
                                        {addingId === patient.id ? "Добавление..." : <><UserPlusIcon size={16} /><span>Добавить</span></>}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>Готово</button>
                </div>
            </div>
        </div>
    );
}

function Pagination({ currentPage, totalPages, onPageChange, totalItems, pageSize }) {
    if (totalPages <= 1) return null;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    return (
        <div className="pagination">
            <div className="pagination-info">{startItem}-{endItem} из {totalItems}</div>
            <div className="pagination-controls">
                <button className="pagination-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}><ChevronLeftIcon /></button>
                <span className="pagination-current">{currentPage} / {totalPages}</span>
                <button className="pagination-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}><ChevronLeftIcon style={{ transform: "rotate(180deg)" }} /></button>
            </div>
        </div>
    );
}

function PatientGroupDetailContent() {
    const { groupId } = useParams();
    const { loading: userLoading, error: userError, data: userData } = useUser();
    const navigate = useNavigate();
    const { setErrorList } = useContext(ErrorHandlerContext);

    const [group, setGroup] = useState(null);
    const [patients, setPatients] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, patient: null });
    const [removeLoading, setRemoveLoading] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [isRenaming, setIsRenaming] = useState(false);
    const [showRename, setShowRename] = useState(false);

    useEffect(() => {
        if (userLoading) return;
        if (userError) navigate(ROUTES.AUTH, { viewTransition: true });
        else if (!isUserDoctor(userData)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [userLoading, userError, userData, navigate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const loadGroup = useCallback(async () => {
        if (userLoading || !isUserDoctor(userData)) return;
        setIsLoading(true);
        try {
            const response = await getAuthorized(`/doctor/patient_groups/${groupId}`, {
                params: { st: (currentPage - 1) * DEFAULT_PAGE_SIZE, limit: DEFAULT_PAGE_SIZE },
            });
            const data = response.data;
            setGroup(data?.group || null);
            setRenameValue(data?.group?.name || "");
            let list = data?.patients || [];
            const total = data?.total_count || 0;
            if (debouncedSearch.trim()) {
                const q = debouncedSearch.trim().toLowerCase();
                list = list.filter((p) => {
                    const name = getPatientDisplayName(p).toLowerCase();
                    return name.includes(q) || (p.email && p.email.toLowerCase().includes(q));
                });
            }
            setPatients(list);
            setTotalCount(total);
        } catch (error) {
            console.error("Failed to load group:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при загрузке группы"]);
            setGroup(null);
            setPatients([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [groupId, currentPage, debouncedSearch, userLoading, userData, setErrorList]);

    useEffect(() => {
        loadGroup();
    }, [loadGroup]);

    const handleRename = async (e) => {
        e.preventDefault();
        const trimmed = renameValue.trim();
        if (!trimmed || trimmed.length > 255) return;
        setIsRenaming(true);
        try {
            await patchAuthorized(`/doctor/patient_groups/${groupId}`, { name: trimmed });
            setShowRename(false);
            loadGroup();
        } catch (error) {
            console.error("Failed to rename group:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при переименовании группы"]);
        } finally {
            setIsRenaming(false);
        }
    };

    const handleRemovePatient = async () => {
        if (!confirmModal.patient) return;
        setRemoveLoading(true);
        try {
            await deleteAuthorized(`/doctor/patient_groups/${groupId}/patients/${confirmModal.patient.id}`, {});
            setConfirmModal({ isOpen: false, patient: null });
            loadGroup();
        } catch (error) {
            console.error("Failed to remove patient from group:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при удалении пациента из группы"]);
        } finally {
            setRemoveLoading(false);
        }
    };

    const handleDownloadReport = useCallback(async () => {
        try {
            const response = await getAuthorized("/doctor/patient_group_report", {
                params: { group_id: groupId },
                responseType: "blob",
            });
            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.download = `Отчет по группе ${group?.name || groupId}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.error("Failed to download group report:", error);
            setErrorList((prev) => [...prev, "Ошибка при скачивании отчёта по группе"]);
        }
    }, [groupId, group?.name, setErrorList]);

    const totalPages = Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));

    const displayPatients = useMemo(() => {
        if (!debouncedSearch.trim()) return patients;
        const q = debouncedSearch.trim().toLowerCase();
        return patients.filter((p) => {
            const name = getPatientDisplayName(p).toLowerCase();
            return name.includes(q) || (p.email && p.email.toLowerCase().includes(q));
        });
    }, [patients, debouncedSearch]);

    if (userLoading) {
        return (
            <div className="patient-group-detail-page">
                <ProfileNavigation isDoctor={true} />
                <div className="patient-group-detail-loading">
                    <div className="spinner"></div>
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="patient-group-detail-page">
            <SEO title={`${group?.name || "Группа"} — Spiria`} description="Управление группой пациентов" noindex={true} />
            <ProfileNavigation isDoctor={isUserDoctor(userData)} />
            <div className="patient-group-detail-container">
                <button className="btn-back" onClick={() => navigate(ROUTES.PATIENT_GROUPS)}>
                    <ChevronLeftIcon size={18} />
                    <span>Назад к группам</span>
                </button>

                <div className="patient-group-detail-header">
                    <div className="patient-group-detail-title">
                        <FolderIconLocal />
                        <div>
                            {showRename ? (
                                <form className="rename-form" onSubmit={handleRename}>
                                    <input
                                        type="text"
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        disabled={isRenaming}
                                        autoFocus
                                        className={renameValue.trim().length > 255 ? "error" : ""}
                                    />
                                    <button type="submit" className="btn-primary" disabled={isRenaming || !renameValue.trim() || renameValue.trim().length > 255}>
                                        {isRenaming ? "Сохранение..." : "Сохранить"}
                                    </button>
                                    <button type="button" className="btn-secondary" onClick={() => setShowRename(false)} disabled={isRenaming}>Отмена</button>
                                </form>
                            ) : (
                                <>
                                    <h1 title={group?.name}>{group?.name || "Группа пациентов"}</h1>
                                    <p className="patient-group-detail-subtitle">
                                        {group?.patient_count || 0} {pluralizePatients(group?.patient_count || 0)} · С {dayjs(group?.creation_dt).format("DD.MM.YYYY")}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="patient-group-detail-actions">
                        <button className="btn-action btn-report" title="Скачать отчёт" onClick={handleDownloadReport}>
                            <DownloadIcon size={16} />
                            <span>Отчёт</span>
                        </button>
                        {!showRename && (
                            <button className="btn-action btn-edit" title="Переименовать" onClick={() => setShowRename(true)}>
                                <span>Изменить название</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="patient-group-detail-toolbar">
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Поиск по пациентам..."
                        />
                        {searchQuery && (
                            <button className="search-clear" onClick={() => setSearchQuery("")} title="Очистить поиск">×</button>
                        )}
                    </div>
                    <button className="btn-primary" onClick={() => setAddModalOpen(true)}>
                        <PlusIcon size={18} />
                        <span>Добавить пациента</span>
                    </button>
                </div>

                {isLoading ? (
                    <div className="patient-group-detail-loading">
                        <div className="spinner"></div>
                        <p>Загрузка пациентов...</p>
                    </div>
                ) : displayPatients.length === 0 ? (
                    <div className="empty-group-state">
                        <div className="empty-icon"><FolderIcon size={48} /></div>
                        <h3>{debouncedSearch ? "Пациенты не найдены" : "В группе пока нет пациентов"}</h3>
                        <p>
                            {debouncedSearch
                                ? "Попробуйте изменить параметры поиска"
                                : "Добавьте пациентов, чтобы формировать отчёты по группе"}
                        </p>
                        {!debouncedSearch && (
                            <button className="btn-primary" onClick={() => setAddModalOpen(true)}>
                                <PlusIcon size={18} />
                                <span>Добавить пациента</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="patient-group-detail-stats">
                            <span>Всего пациентов: {totalCount}</span>
                        </div>
                        <div className="patient-group-patients-list">
                            {displayPatients.map((patient) => (
                                <div key={patient.id} className="group-patient-card">
                                    <div className="group-patient-main">
                                        <div className="group-patient-avatar">
                                            {hasPatientName(patient) ? getPatientInitials(patient) : <UserIcon size={20} />}
                                        </div>
                                        <div className="group-patient-info">
                                            <h3 className="group-patient-name" title={getPatientDisplayName(patient)}>{getPatientDisplayName(patient)}</h3>
                                            <div className="group-patient-meta">
                                                {patient.email && <span className="group-patient-email">{patient.email}</span>}
                                                {patient.phone_number && <span className="group-patient-phone">{patient.phone_number}</span>}
                                                <span className="group-patient-date">С {dayjs(patient.creation_dt).format("DD.MM.YYYY")}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="group-patient-actions">
                                        <button
                                            className="btn-action btn-remove"
                                            title="Удалить из группы"
                                            onClick={() => setConfirmModal({ isOpen: true, patient })}
                                        >
                                            <TrashIcon size={16} />
                                            <span>Удалить</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                            pageSize={DEFAULT_PAGE_SIZE}
                        />
                    </>
                )}
            </div>

            <PatientAddModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                groupId={groupId}
                onAdded={() => {
                    loadGroup();
                }}
                isLoading={removeLoading}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, patient: null })}
                onConfirm={handleRemovePatient}
                title="Удалить пациента из группы?"
                message={`Вы уверены, что хотите удалить ${getPatientDisplayName(confirmModal.patient)} из группы?`}
                confirmText="Удалить"
                isLoading={removeLoading}
                danger
            />
        </div>
    );
}

function pluralizePatients(count) {
    if (count % 10 === 1 && count % 100 !== 11) return "пациент";
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return "пациента";
    return "пациентов";
}

export default function PatientGroupDetailPage() {
    return (
        <ErrorHandlerContextProvider>
            <PatientGroupDetailContent />
            <ErrorHandler />
        </ErrorHandlerContextProvider>
    );
}
